import styled, { css } from "styled-components";
import { Link } from "wouter";
import { breakpoints, colors, widths } from "definitions";
import ChevronDown from "icons/Chevron";

// El modo "overlay" (header transparente sobre el video del hero) sólo aplica en
// desktop: por debajo de `laptop` el menú es un panel blanco desplegable que no
// cambia, así que sus estilos quedan intactos.
const onlyDesktop = `@media screen and (min-width: ${widths.laptop + 1}px)`;

export const MenuContainer = styled.div`
  display: flex;
  padding-right: 25px;
`;

export const MenuItem = styled.li`
  padding: 23px;
  position: relative;
  cursor: pointer;
  transition: color 200ms ease, background-color 200ms ease;

  ${breakpoints.laptop} {
    text-align: center;
    width: 100%;
    padding: 15px 0;
  }

  &:hover {
    color: ${colors.navigation.brightBlue};
  }
`;

export const MenuItemTitle = styled.span`
  cursor: pointer;
  font-size: 14px;
  line-height: 17px;
  text-decoration: none;
  white-space: pre;
  font-weight: 500;

  display: flex;
  align-items: center;

  padding-left: 5px;
  color: ${colors.fonts.darkGray};
  transition: color 180ms ease;

  z-index: 50000000;

  ${MenuItem}:hover & {
    color: ${colors.navigation.brightBlue};
  }

  [disabled] {
    color: ${colors.navigation.veryLightGray};
  }

  ${breakpoints.laptop} {
    color: #006edf;
    justify-content: center;
  }
`;

export const Arrow = styled(ChevronDown)`
  transform: ${(props) => (props.selected ? "rotate(180deg)" : "none")};
  transition: transform 260ms cubic-bezier(0.4, 0, 0.2, 1),
              stroke 180ms ease;
  width: 2rem;
  height: 2rem;
  stroke: ${colors.fonts.darkGray};

  ${MenuItem}:hover & {
    stroke: ${colors.navigation.brightBlue};
  }

  ${breakpoints.laptop} {
    stroke: white;
    width: 1.5rem;
    height: 1.5rem;
  }
`;

export const SubMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: 5px;
  transition: all 200ms;

  background-color: ${colors.background.white};

  ${breakpoints.laptop} {
    background-color: transparent;
  }
`;

const linkStyles = css`
  padding: 12px 24px;
  font-size: 14px;
  line-height: 18px;
  text-decoration: none;
  white-space: pre;
  font-weight: 500;
  display: block;
  color: ${colors.fonts.darkGray};
  transition: background-color 160ms ease, color 160ms ease;

  &:hover {
    background-color: rgba(0, 110, 223, 0.1);
    color: ${colors.navigation.brightBlue};
  }

  ${breakpoints.laptop} {
    text-align: center;
    padding: 10px 24px;

    &:hover {
      background-color: rgba(0, 110, 223, 0.08);
      color: ${colors.navigation.brightBlue};
    }
  }
`;

export const MenuItemLink = styled(Link)`
  ${linkStyles}
`;

export const MainMenuItemLink = styled(Link)`
  padding: 10px;
  font-size: 14px;
  line-height: 17px;
  text-decoration: none;
  white-space: pre;
  font-weight: 500;
  margin-bottom: 4px;
  color: ${colors.fonts.darkGray};
  transition: color 180ms ease;

  &:hover {
    color: ${colors.navigation.brightBlue};
  }

  ${breakpoints.laptop} {
    color: #006edf;

    &:hover {
      color: ${colors.navigation.brightBlue};
    }
  }
`;

export const ExternalMenuItemLink = styled.a`
  ${linkStyles}
`;

export const SubMenuList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 6px 0;
  display: flex;

  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  min-width: 200px;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 16px 32px rgba(8, 37, 90, 0.18),
              0 4px 12px rgba(8, 37, 90, 0.1);
  transition: opacity 220ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 220ms cubic-bezier(0.22, 1, 0.36, 1);

  background-color: ${colors.background.white};

  opacity: ${(props) => (!props.selected ? "0" : "1")};
  pointer-events: ${(props) => (!props.selected ? "none" : "auto")};
  position: ${(props) => (!props.selected ? "fixed" : "absolute")};
  transform: ${(props) =>
    !props.selected ? "translateY(-8px)" : "translateY(0)"};

  & > ${MenuItem} {
    padding: 0;
    width: 100%;

    &[data-group-start] {
      margin-top: 6px;
      padding-top: 6px;
      border-top: 1px solid ${colors.background.lightGray};
    }

    &:hover {
      background-color: transparent;
    }
  }

  ${breakpoints.laptop} {
    margin: 0;
    width: 100%;
    box-shadow: none;
    border-radius: 0;
    min-width: unset;
    background-color: transparent;
    position: ${(props) => (!props.selected ? "fixed" : "inherit")};
    padding: 0.25rem 0;

    & > ${MenuItem} {
      padding: 4px 0;
    }
  }
`;

// MenuList va al final del archivo porque en modo overlay pinta a sus hijos por
// selector de descendencia, y necesita las referencias ya declaradas.
export const MenuList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
  overflow: visible;
  justify-content: space-between;
  width: 100%;

  background-color: ${colors.background.white};

  ${({ overlay }) => overlay && css`
    ${onlyDesktop} {
      background-color: transparent;

      ${MenuItemTitle}, ${MainMenuItemLink} {
        color: ${colors.fonts.white};
      }

      ${Arrow} {
        stroke: ${colors.fonts.white};
      }
    }
  `}

  ${breakpoints.laptop} {
    flex-flow: column nowrap;

    position: ${({ open }) => (open ? "absolute" : "fixed")};
    transform: ${({ open }) => (open ? "translateY(0)" : "translateY(-110%)")};
    opacity: ${({ open }) => (open ? 1 : 0)};
    pointer-events: ${({ open }) => (open ? "auto" : "none")};
    top: 0;
    right: 0;
    height: min-content;
    width: 100%;
    justify-content: flex-start;

    margin-top: 6.5rem;
    padding-top: 25px;
    padding-bottom: 15px;
    transition: transform 420ms cubic-bezier(0.16, 1, 0.3, 1),
                opacity 320ms cubic-bezier(0.16, 1, 0.3, 1);

    color: #006edf;
    z-index: 900;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  }
`;
